// ProgramTimeline — simple horizontal track that visualizes the case
// schedule from "now" through the competition close.
//
// Even when the form has no opens_at/closes_at yet, the Comp Open and
// Comp Close markers always render at sensible defaults (today + 14
// and + 28 days) in a "ghost" state. Drag them to set the real date —
// that is what the whole component exists to make easy.
//
// Web-only drag (mouse events). On native we render read-only.

import { useEffect, useMemo, useRef, useState } from "react";
import { Platform, Text, View, type LayoutChangeEvent, type ViewStyle } from "react-native";
import Svg, { Path } from "react-native-svg";
import { COLORS, FONTS } from "../../lib/brand";
import { PANE_THEMES } from "../brand/paneThemes";

const THEME = PANE_THEMES.Client;
const AMBER = THEME.accent;
const SAGE = THEME.success;
const FOREST_MID = COLORS.FOREST_MID.hex;
const W = "rgba(255,255,255,1)";
const WS = "rgba(255,255,255,0.78)";
const WM = "rgba(255,255,255,0.55)";
const WL = "rgba(255,255,255,0.28)";
const BORDER = "rgba(255,255,255,0.12)";
const TRACK_HEIGHT = 6;
const MARKER_SIZE = 18;

// ---------------------------------------------------------------------------
// Types
// ---------------------------------------------------------------------------

export type TimelineDeliverable = {
  id: string;
  name: string;
  sequence: number;
  /** "YYYY-MM-DDTHH:mm" (local time). null = unlocks at comp open. */
  opensAt: string | null;
};

export type ProgramTimelineProps = {
  opensAt: string;
  closesAt: string;
  onOpensAtChange: (next: string) => void;
  onClosesAtChange: (next: string) => void;
  deliverables: TimelineDeliverable[];
  onDeliverableOpensAtChange: (id: string, next: string) => void;
  editable: boolean;
};

// ---------------------------------------------------------------------------
// Date helpers
// ---------------------------------------------------------------------------

function parseFormDate(s: string | null | undefined): Date | null {
  if (!s) return null;
  const m = /^(\d{4})-(\d{2})-(\d{2})T(\d{2}):(\d{2})$/.exec(s.trim());
  if (!m) return null;
  const [_, y, mo, d, h, mi] = m;
  const dt = new Date(Number(y), Number(mo) - 1, Number(d), Number(h), Number(mi));
  return isNaN(dt.getTime()) ? null : dt;
}

function formatFormDate(d: Date): string {
  const pad = (n: number) => String(n).padStart(2, "0");
  return `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}T${pad(d.getHours())}:${pad(d.getMinutes())}`;
}

function snapToHour(d: Date): Date {
  const out = new Date(d);
  if (out.getMinutes() >= 30) out.setHours(out.getHours() + 1);
  out.setMinutes(0, 0, 0);
  return out;
}

function shortDateLabel(d: Date): string {
  return d.toLocaleDateString(undefined, { month: "short", day: "numeric" });
}

function defaultOpensAt(): Date {
  const d = new Date();
  d.setDate(d.getDate() + 14);
  d.setHours(9, 0, 0, 0);
  return d;
}

function defaultClosesAt(): Date {
  const d = new Date();
  d.setDate(d.getDate() + 28);
  d.setHours(17, 0, 0, 0);
  return d;
}

// ---------------------------------------------------------------------------
// Component
// ---------------------------------------------------------------------------

export function ProgramTimeline({
  opensAt,
  closesAt,
  onOpensAtChange,
  onClosesAtChange,
  deliverables,
  onDeliverableOpensAtChange,
  editable,
}: ProgramTimelineProps) {
  const [trackWidth, setTrackWidth] = useState(0);
  const trackRef = useRef<View | null>(null);
  const trackLeftRef = useRef(0);

  const opens = parseFormDate(opensAt);
  const closes = parseFormDate(closesAt);

  // Effective dates — fall back to defaults so markers always render.
  const effectiveOpens = opens ?? defaultOpensAt();
  const effectiveCloses = closes ?? defaultClosesAt();

  // Domain: a STABLE 3-month visible window for the timeline.
  //
  // Left edge  = today's midnight (no wasted space before "now")
  // Right edge = today + 3 months (covers the vast majority of case lengths)
  //
  // Re-computed on mount so it picks up the current "today" each
  // session (works the same in May as in December).
  //
  // The domain is FIXED at today→+3mo by default. Two exceptions handle
  // edge cases where a marker would otherwise be off-screen:
  //   1. Legacy data with opens_at in the past → extend left to include
  //      the open marker.
  //   2. A case longer than ~3 months → extend right to include the
  //      close marker (3-day padding past close).
  // Both are auto-expand only — the domain never shrinks during edits,
  // so the unmoved marker stays visually pinned when you drag the other.
  const [domain, setDomain] = useState(() => {
    const todayMidnight = new Date();
    todayMidnight.setHours(0, 0, 0, 0);
    const threeMonthsOut = new Date(todayMidnight);
    threeMonthsOut.setMonth(threeMonthsOut.getMonth() + 3);
    const start =
      effectiveOpens.getTime() < todayMidnight.getTime()
        ? new Date(effectiveOpens.getTime() - 86400000)
        : new Date(todayMidnight);
    // If the case extends past 3 months from today, expand right edge
    // to include it with 3 days of padding past close.
    const end =
      effectiveCloses.getTime() + 3 * 86400000 > threeMonthsOut.getTime()
        ? new Date(effectiveCloses.getTime() + 3 * 86400000)
        : threeMonthsOut;
    return { domainStart: start, domainEnd: end };
  });

  // Auto-expand the domain when a marker moves outside the current
  // range (with a 1-day buffer). Never shrinks.
  useEffect(() => {
    const bufferMs = 86400000;
    let needsUpdate = false;
    let nextStart = domain.domainStart;
    let nextEnd = domain.domainEnd;
    if (effectiveOpens.getTime() < domain.domainStart.getTime() - bufferMs) {
      nextStart = new Date(effectiveOpens.getTime() - bufferMs);
      needsUpdate = true;
    }
    const wantedEnd = effectiveCloses.getTime() + 3 * 86400000;
    if (wantedEnd > domain.domainEnd.getTime() + bufferMs) {
      nextEnd = new Date(wantedEnd);
      needsUpdate = true;
    }
    if (needsUpdate) setDomain({ domainStart: nextStart, domainEnd: nextEnd });
  }, [opensAt, closesAt]);

  const domainStart = domain.domainStart;
  const domainEnd = domain.domainEnd;

  function dateToX(d: Date): number {
    if (trackWidth <= 0) return 0;
    const span = domainEnd.getTime() - domainStart.getTime();
    if (span <= 0) return 0;
    const t = (d.getTime() - domainStart.getTime()) / span;
    return Math.max(0, Math.min(trackWidth, t * trackWidth));
  }

  function xToDate(x: number): Date {
    const span = domainEnd.getTime() - domainStart.getTime();
    const t = trackWidth > 0 ? Math.max(0, Math.min(1, x / trackWidth)) : 0;
    return new Date(domainStart.getTime() + t * span);
  }

  function onTrackLayout(e: LayoutChangeEvent) {
    setTrackWidth(e.nativeEvent.layout.width);
    if (Platform.OS === "web" && trackRef.current) {
      const node = trackRef.current as unknown as { getBoundingClientRect?: () => DOMRect };
      const rect = node.getBoundingClientRect?.();
      if (rect) trackLeftRef.current = rect.left;
    }
  }

  function startDrag(initialX: number, onMove: (date: Date) => void) {
    if (Platform.OS !== "web" || !editable) return;
    function handleMove(e: MouseEvent) {
      if (trackRef.current) {
        const node = trackRef.current as unknown as { getBoundingClientRect?: () => DOMRect };
        const rect = node.getBoundingClientRect?.();
        if (rect) trackLeftRef.current = rect.left;
      }
      const localX = e.clientX - trackLeftRef.current;
      onMove(snapToHour(xToDate(localX)));
    }
    function handleUp() {
      window.removeEventListener("mousemove", handleMove);
      window.removeEventListener("mouseup", handleUp);
    }
    window.addEventListener("mousemove", handleMove);
    window.addEventListener("mouseup", handleUp);
    handleMove({ clientX: initialX } as MouseEvent);
  }

  // Marker positions (always defined now)
  const opensX = dateToX(effectiveOpens);
  const closesX = dateToX(effectiveCloses);
  const now = new Date();
  const nowX = dateToX(now);

  const liveStart = opensX;
  const liveEnd = closesX;

  return (
    <View>
      <View
        ref={trackRef}
        collapsable={false}
        onLayout={onTrackLayout}
        style={{
          position: "relative",
          height: 80,
          paddingTop: 22,
          paddingBottom: 22,
        }}
      >
        {/* Track */}
        <View
          style={{
            position: "absolute",
            left: 0,
            right: 0,
            top: 36,
            height: TRACK_HEIGHT,
            borderRadius: TRACK_HEIGHT / 2,
            backgroundColor: "rgba(255,255,255,0.05)",
            borderWidth: 1,
            borderColor: BORDER,
          }}
        />
        {/* Live-phase shading */}
        {trackWidth > 0 && liveEnd > liveStart ? (
          <View
            style={{
              position: "absolute",
              left: liveStart,
              top: 36,
              width: Math.max(0, liveEnd - liveStart),
              height: TRACK_HEIGHT,
              borderRadius: TRACK_HEIGHT / 2,
              backgroundColor:
                opens && closes ? "rgba(127,203,122,0.20)" : "rgba(127,203,122,0.06)",
              borderWidth: 1,
              borderColor:
                opens && closes ? "rgba(127,203,122,0.45)" : "rgba(127,203,122,0.20)",
            }}
          />
        ) : null}
        {/* Today tick */}
        {trackWidth > 0 && nowX >= 0 && nowX <= trackWidth ? (
          <View
            style={{
              position: "absolute",
              left: nowX - 0.5,
              top: 26,
              width: 1,
              height: TRACK_HEIGHT + 16,
              backgroundColor: "rgba(255,255,255,0.18)",
            }}
          />
        ) : null}

        {/* Comp Open marker — always rendered, ghost when not yet set.
            Clamped so it can never overshoot the close handle (with a
            minimum 1-hour gap between them). */}
        <Marker
          x={opensX}
          top={36}
          label={opens ? "Comp open" : "Drag to set"}
          sublabel={shortDateLabel(effectiveOpens)}
          tone="amber"
          ghost={!opens}
          editable={editable}
          onMouseDownX={(initialX) =>
            startDrag(initialX, (d) => {
              const closeDate = parseFormDate(closesAt) ?? effectiveCloses;
              const minGapMs = 60 * 60 * 1000; // 1 hour
              const maxOpens = new Date(closeDate.getTime() - minGapMs);
              // Floor at "now" — competition can't open in the past.
              // Use a stable "today midnight" floor so the floor doesn't
              // drift forward mid-drag and snap the marker around.
              const todayMidnight = new Date();
              todayMidnight.setHours(0, 0, 0, 0);
              let next = d;
              if (next.getTime() < todayMidnight.getTime()) next = todayMidnight;
              if (next.getTime() > maxOpens.getTime()) next = maxOpens;
              onOpensAtChange(formatFormDate(next));
            })
          }
        />
        {/* Comp Close marker — always rendered, below.
            Clamped so it can never undershoot the open handle (same
            1-hour minimum gap). */}
        <Marker
          x={closesX}
          top={36}
          label={closes ? "Comp close" : "Drag to set"}
          sublabel={shortDateLabel(effectiveCloses)}
          tone="amber"
          placement="below"
          ghost={!closes}
          editable={editable}
          onMouseDownX={(initialX) =>
            startDrag(initialX, (d) => {
              const openDate = parseFormDate(opensAt) ?? effectiveOpens;
              const minGapMs = 60 * 60 * 1000; // 1 hour
              const minCloses = new Date(openDate.getTime() + minGapMs);
              const clamped = d.getTime() < minCloses.getTime() ? minCloses : d;
              onClosesAtChange(formatFormDate(clamped));
            })
          }
        />

        {/* Deliverable markers — only show when the deliverable has a date set */}
        {deliverables.map((d) => {
          const dt = parseFormDate(d.opensAt);
          if (!dt) return null;
          const x = dateToX(dt);
          const locked = dt > new Date();
          return (
            <Marker
              key={d.id}
              x={x}
              top={36}
              tone="sage"
              label={`${d.sequence}. ${d.name || "Deliverable"}`}
              sublabel={shortDateLabel(dt)}
              icon={locked ? "lock" : null}
              placement="below"
              editable={editable}
              onMouseDownX={(initialX) =>
                startDrag(initialX, (next) => onDeliverableOpensAtChange(d.id, formatFormDate(next)))
              }
            />
          );
        })}

        {/* Domain edge labels — small + subtle */}
        <Text
          style={{
            position: "absolute",
            left: 0,
            bottom: 0,
            fontFamily: FONTS.body,
            fontSize: 10,
            color: WL,
          }}
        >
          {shortDateLabel(domainStart)}
        </Text>
        <Text
          style={{
            position: "absolute",
            right: 0,
            bottom: 0,
            fontFamily: FONTS.body,
            fontSize: 10,
            color: WL,
          }}
        >
          {shortDateLabel(domainEnd)}
        </Text>
      </View>
    </View>
  );
}

// ---------------------------------------------------------------------------
// Marker
// ---------------------------------------------------------------------------

type MarkerTone = "amber" | "sage";

function Marker({
  x,
  top,
  label,
  sublabel,
  tone,
  icon,
  placement = "above",
  ghost = false,
  editable,
  onMouseDownX,
}: {
  x: number;
  top: number;
  label: string;
  sublabel: string;
  tone: MarkerTone;
  icon?: "lock" | null;
  placement?: "above" | "below";
  ghost?: boolean;
  editable: boolean;
  onMouseDownX: (clientX: number) => void;
}) {
  const color = tone === "amber" ? AMBER : SAGE;
  const labelTop = placement === "above" ? top - 22 : top + 14;
  // Cast through unknown — RN's ViewStyle doesn't include "grab" in its
  // CursorValue union, but RN-Web supports it just fine.
  const cursor =
    editable && Platform.OS === "web"
      ? ({ cursor: "grab" } as unknown as ViewStyle)
      : null;
  const fillColor = ghost
    ? "transparent"
    : tone === "amber"
      ? "rgba(181,135,58,0.90)"
      : "rgba(127,203,122,0.85)";
  return (
    <>
      <View
        style={[
          {
            position: "absolute",
            left: x - MARKER_SIZE / 2,
            top: top - (MARKER_SIZE - TRACK_HEIGHT) / 2,
            width: MARKER_SIZE,
            height: MARKER_SIZE,
            borderRadius: MARKER_SIZE / 2,
            backgroundColor: fillColor,
            borderWidth: ghost ? 1.5 : 2,
            borderColor: ghost ? color : FOREST_MID,
            borderStyle: ghost ? "dashed" : "solid",
            alignItems: "center",
            justifyContent: "center",
            opacity: ghost ? 0.85 : 1,
          },
          cursor,
        ]}
        // RN-Web passes onMouseDown through to the underlying div even though
        // it isn't on RN's typed prop list — this cast keeps tsc happy.
        {...({
          onMouseDown: (e: { clientX: number; preventDefault: () => void }) => {
            e.preventDefault?.();
            onMouseDownX(e.clientX);
          },
        } as object)}
      >
        {icon === "lock" ? <LockIcon /> : null}
      </View>

      <View
        pointerEvents="none"
        style={{
          position: "absolute",
          left: x - 60,
          width: 120,
          top: labelTop,
          alignItems: "center",
        }}
      >
        <Text
          style={{
            fontFamily: FONTS.bodyBold,
            fontSize: 9,
            letterSpacing: 1.2,
            textTransform: "uppercase",
            color: ghost ? WM : color,
          }}
          numberOfLines={1}
        >
          {label}
        </Text>
        <Text
          style={{ fontFamily: FONTS.body, fontSize: 10, color: ghost ? WL : WS }}
        >
          {sublabel}
        </Text>
      </View>
    </>
  );
}

function LockIcon() {
  return (
    <Svg width={9} height={9} viewBox="0 0 12 12" fill="none">
      <Path
        d="M3 5.5V4a3 3 0 1 1 6 0v1.5M2.5 5.5h7v5h-7v-5z"
        stroke={THEME.sidebarBg}
        strokeWidth={1.4}
        strokeLinecap="round"
        strokeLinejoin="round"
        fill="none"
      />
    </Svg>
  );
}
